import { FC, memo, ReactNode, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { DIAGNOSTICS_STEPS_KEYS } from "@/constants/diagnostics";
import { useClientHeight } from "@/hooks/useClientHeight";
import { selectDiagnosticsStep } from "@/store/features/diagnostics/selectors";
import { useAppSelector } from "@/store/hooks";
import { StyledDiagnosticsScrollableArea } from "./styled";

interface IProps {
  children: ReactNode;
}

const DiagnosticsScrollableArea: FC<IProps> = ({ children }) => {
  const height = useClientHeight();
  const [y, setY] = useState(0);
  const [withTransition, setWithTransition] = useState(false);

  const step = useAppSelector(selectDiagnosticsStep);

  const calculateY = useCallback(() => {
    const id = DIAGNOSTICS_STEPS_KEYS[step as keyof typeof DIAGNOSTICS_STEPS_KEYS];

    if (id) {
      const element = document.getElementById(id);

      if (element) {
        const {
          offsetTop,
          clientHeight,
        } = element;
        const documentHeight = document.documentElement.clientHeight;

        const y = clientHeight > documentHeight ? 0 : (documentHeight - clientHeight) / 2;
        setY(y - offsetTop + 30);
      }
    }
  }, [step]);

  useLayoutEffect(() => {
    calculateY();
  }, [calculateY]);

  useEffect(() => {
    const timeout = setTimeout(() => setWithTransition(true), 500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", calculateY);
    // extra guard to make sure the calculations are done after the initial render
    setTimeout(() => {
      calculateY();
    }, 50);
    return () => window.removeEventListener("resize", calculateY);
  }, [calculateY]);

  return (
    <StyledDiagnosticsScrollableArea $height={height} $y={y} $withTransition={withTransition}>
      {children}
    </StyledDiagnosticsScrollableArea>
  );
};

export default memo(DiagnosticsScrollableArea);